/* eslint-disable react/display-name */
import React, { memo, useCallback } from 'react';
import styled from 'styled-components';
import { reduxConnect } from '../../../common';
import { DateTime } from 'luxon';
import { arrowDirections, ArrowIcon } from '../../common/utilities/arrowIcon';
import { showManagedLocationsPopup } from '../../../common/actions/manageLocationsPopupActions';
import { defaultStationName } from '../../../common/utilities/constants';
import { translate } from '../../../common/translation/translate';
import { smallMediaQuery } from '../../../common/utilities/sizeHelpers';
import type { IStoreState } from '../../common/interfaces/types';
import { parseAndFormatISO8601DateStr } from '../../../common/utilities/dateTimeUtils';

const dateFormat = 'MMM dd, yyyy h:mm a';
const localizedHomePageRoute = '/weather-forecast/now/:location';

interface CurrentConditionsHeaderProps {
    stationsName: string;
    routePath: string;
    cityName: string;
    territoryAbbr: string;
    countryIso3Code: string;
    showManagedLocationsPopup: (show: boolean) => void;
    currentConditionsTimeLocalStr: string;
    showManagedLocations: boolean;
}

const mapStoreStateToProps = (storeState: IStoreState) => {
    const { manageLocationsPopup, nowConditions } = storeState;
    const appState = storeState.appState.appState || {};
    const { user, routePath } = appState;
    const { cdtl: currentConditionsTimeLocalStr } = nowConditions?.nowConditions || {};

    const locality = user?.ActiveLocation?.Locality || {};
    const stationData = user?.ActiveLocation?.Station;
    const { CityName: cityName, TerritoryAbbr: territoryAbbr, CountryIso3Code: countryIso3Code } = locality;

    const stationsName = stationData?.StationName;

    const { showManagedLocations } = manageLocationsPopup || {};

    return {
        routePath,
        stationsName,
        cityName,
        territoryAbbr,
        countryIso3Code,
        showManagedLocations,
        currentConditionsTimeLocalStr
    };
};

const mapDispatchToProps = {
    showManagedLocationsPopup
};

export const CurrentConditionsHeader = reduxConnect(mapStoreStateToProps, mapDispatchToProps)(memo(({
    stationsName,
    routePath,
    cityName,
    territoryAbbr,
    countryIso3Code,
    showManagedLocationsPopup,
    currentConditionsTimeLocalStr,
    showManagedLocations
}: CurrentConditionsHeaderProps) => {

    const handleLocationManagerDialog = useCallback(() => {
        showManagedLocationsPopup(!showManagedLocations);
    }, [showManagedLocations]);

    const locationDateTime = currentConditionsTimeLocalStr
    ? parseAndFormatISO8601DateStr(currentConditionsTimeLocalStr, dateFormat)
    : DateTime.now().toFormat(dateFormat);

    let pageH1Text = translate('todaysWeather');

    if (routePath === localizedHomePageRoute && cityName && (territoryAbbr || countryIso3Code)) {
        pageH1Text = territoryAbbr ? `${pageH1Text} - ${cityName}, ${territoryAbbr}` : `${pageH1Text} - ${cityName}, ${countryIso3Code}`;
    }

    return (
        <>
            <CurrentConditionsHeaderContainer>
                <CurrentConditionsLocationName
                    isLongLocName={pageH1Text.length > 55}>{pageH1Text}</CurrentConditionsLocationName>
                <CurrentConditionsDateTime>{locationDateTime}</CurrentConditionsDateTime>
                <CurrentConditionsStationInfo>
                    <StationName>{stationsName ?? defaultStationName}</StationName>
                    <StationsPopupLink onClick={handleLocationManagerDialog}>
                        <ArrowIcon width={13} height={13} strokeColor='' fillColor='#ffffff'
                            arrowDirection={arrowDirections.down}/>
                    </StationsPopupLink>
                </CurrentConditionsStationInfo>
            </CurrentConditionsHeaderContainer>
        </>
    );
}));

interface CurrentConditionsLocationNameProps {
    isLongLocName: boolean;
}

const CurrentConditionsHeaderContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    text-decoration: none;
    height: 33%;
    width: 100%;
    margin-bottom: 7px;
`;

const CurrentConditionsLocationName = styled.h1<CurrentConditionsLocationNameProps>`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    width: 100%;
    height: auto;
    max-height: 33px;
    font-size: ${({ isLongLocName }) => isLongLocName ? '12px' : '15px'};
    font-weight: bold;
    color: #ffffff;
    font-family: 'Source Sans Pro Semibold', sans-serif;
    @media ${smallMediaQuery} {
        font-size: 15px;
    }
`;

const CurrentConditionsDateTime = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    width: 100%;
    font-size: 13px;
    font-weight: 400;
    color: #ffffff;
    height: 19px;
`;

const CurrentConditionsStationInfo = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    width: 100%;
    min-height: 19px;
    height: auto;
`;

const StationName = styled.div`
    display: block;
    width: 213px;
    margin-right: 3px;
    font-size: 13px;
    font-weight: 400;
    color: #ffffff;
    height: 100%;
    min-height: 30px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    @media ${smallMediaQuery} {
        width: 290px;
    }
`;

const StationsPopupLink = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    width: 10%;
    height: 19px;
    cursor: pointer;
    z-index: 9;
`;
